import { FC, KeyboardEvent, useCallback } from 'react';

import type { ElementTool } from '@adflow/types';
import {
  Box,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spacer,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { LuChevronRight, LuCircle, LuPlus, LuSquare, LuTriangle, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';

import { useEditorStageView } from '../../contexts/EditorStageViewContext';
import { useStore } from '../../hooks/useStore';

type MenuEntry = {
  tool: ElementTool;
  label: string;
  /** Single key that picks the entry while the menu is open. */
  shortcut?: string;
  icon: JSX.Element;
};

const TEXT_ENTRY: MenuEntry = {
  tool: 'TEXT',
  label: 'Text',
  shortcut: 'T',
  icon: <LuType />,
};

const VECTOR_ENTRIES: MenuEntry[] = [
  { tool: 'RECTANGLE', label: 'Rectangle', shortcut: 'R', icon: <LuSquare /> },
  { tool: 'ELLIPSE', label: 'Ellipse', shortcut: 'O', icon: <LuCircle /> },
  { tool: 'TRIANGLE', label: 'Triangle', icon: <LuTriangle /> },
];

const ALL_ENTRIES = [TEXT_ENTRY, ...VECTOR_ENTRIES];

const Shortcut: FC<{ children?: string }> = ({ children }) =>
  children ? (
    <Text as="span" fontSize="sm" color="gray.500" ml={6}>
      {children}
    </Text>
  ) : null;

/**
 * The "+" in the Elements panel header. Picking an entry arms that tool on the canvas. The vector
 * shapes sit in a submenu.
 */
const AddElementMenu: FC = () => {
  const { setStageView } = useEditorStageView();
  const setActiveTool = useStore((state) => state.setActiveTool);
  const activeTool = useStore((state) => state.activeTool);
  const menu = useDisclosure();
  const submenu = useDisclosure();

  const closeAll = useCallback(() => {
    submenu.onClose();
    menu.onClose();
  }, [menu, submenu]);

  const pick = useCallback(
    (tool: ElementTool) => {
      // Drawing happens on the canvas, so show it first.
      setStageView('editor');
      setActiveTool(tool);
      closeAll();
    },
    [closeAll, setActiveTool, setStageView],
  );

  // React events from the submenu bubble up here too, so one handler covers both lists.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) {
        return;
      }
      const entry = ALL_ENTRIES.find(
        (candidate) => candidate.shortcut?.toLowerCase() === event.key.toLowerCase(),
      );
      if (!entry) {
        return;
      }
      // Stops the menu's own type-ahead from also reacting to the key.
      event.preventDefault();
      pick(entry.tool);
    },
    [pick],
  );

  return (
    <Menu isLazy isOpen={menu.isOpen} onOpen={menu.onOpen} onClose={closeAll} closeOnSelect={false}>
      <MenuButton
        as={IconButton}
        size="xs"
        variant="ghost"
        colorScheme={activeTool ? 'blue' : 'gray'}
        icon={<LuPlus size="16" />}
        aria-label="Add element"
        data-test-id="add-element-menu-button"
      />
      {/* Rendered on the body: the Elements panel clips overflow, which would cut off or flip the
          submenu. The submenu stays inside this list so clicks in it are not "outside" clicks. */}
      <Portal>
        <MenuList minW="180px" onKeyDown={handleKeyDown} data-test-id="add-element-menu">
          <MenuItem
            icon={TEXT_ENTRY.icon}
            onClick={() => pick(TEXT_ENTRY.tool)}
            onMouseEnter={submenu.onClose}
            data-test-id="add-element-text"
          >
            <Flex alignItems="center">
              {TEXT_ENTRY.label}
              <Spacer />
              <Shortcut>{TEXT_ENTRY.shortcut}</Shortcut>
            </Flex>
          </MenuItem>
          <Menu
            isLazy
            placement="right-start"
            offset={[0, 4]}
            isOpen={submenu.isOpen}
            onOpen={submenu.onOpen}
            onClose={submenu.onClose}
            closeOnSelect={false}
          >
            {/* Handlers sit on a wrapper: `as={MenuItem}` replaces the ones set on the button. */}
            <Box
              role="none"
              onMouseEnter={submenu.onOpen}
              onKeyDown={(event: KeyboardEvent) => {
                if (event.key === 'ArrowRight') {
                  event.preventDefault();
                  submenu.onOpen();
                }
              }}
            >
              <MenuButton as={MenuItem} icon={<PiBoundingBox />} data-test-id="add-element-vector">
                <Flex alignItems="center">
                  Vector
                  <Spacer />
                  <LuChevronRight />
                </Flex>
              </MenuButton>
            </Box>
            <MenuList minW="180px" data-test-id="add-element-vector-menu">
              {VECTOR_ENTRIES.map((entry) => (
                <MenuItem
                  key={entry.tool}
                  icon={entry.icon}
                  onClick={() => pick(entry.tool)}
                  data-test-id={`add-element-${entry.tool.toLowerCase()}`}
                >
                  <Flex alignItems="center">
                    {entry.label}
                    <Spacer />
                    <Shortcut>{entry.shortcut}</Shortcut>
                  </Flex>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </MenuList>
      </Portal>
    </Menu>
  );
};

AddElementMenu.displayName = 'AddElementMenu';

export default AddElementMenu;
